import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import makeRequest from 'utils/makeRequest';
import {
  getSchemaStyles,
  getSpacingStyles,
  getBorderStyles,
} from 'components_library/utils/getStyleObject';
import GridMaker from 'components_library/organisms/GridMaker/GridMaker';
import StoryCard from 'components_library/molecules/StoryCard/StoryCard';
import IFrame from 'components_library/atoms/IFrame/IFrame';
import WithMiniTv from 'modules/WithMiniTv/WithMiniTv';
import AdCaller from 'helpers/ads/adCaller';
import InViewObserver from 'components/InViewObserver';
import analyticsWrapper from 'components_library/utils/analyticsWrapper';
import Banner from 'components_library/molecules/Banner/Banner';
import s from './StoryList.scss';

const leadCardLayouts = ['card_6'];

let keyCounter = 0;

const getUniqueKey = () => {
  keyCounter += 1;
  return keyCounter;
};

const componentMap = {
  grid: (
    section,
    level,
    isMobile,
    items,
    gaData,
    spacingClasses,
    SectionWrapperComponent,
    SliderComponent,
    hideTimeStamp,
  ) => {
    const sectionObj = section;
    if (!sectionObj) return null;
    return (
      <GridMaker
        componentMap={componentMap}
        {...sectionObj}
        key={sectionObj.id || `${sectionObj.type}_${getUniqueKey()}`}
        level={level}
        isMobile={isMobile}
        items={items}
        ga={gaData}
        spacingClasses={spacingClasses}
        SectionWrapperComponent={SectionWrapperComponent}
        SliderComponent={SliderComponent}
        extraProps={{ hideTimeStamp }}
      />
    );
  },
  story_list: (
    section,
    level,
    isMobile,
    items,
    gaData,
    spacingClasses,
    SectionWrapperComponent,
    SliderComponent,
    extraProps = {},
  ) => (
    <StoryList
      {...section}
      {...extraProps}
      isMobile={isMobile}
      items={items}
      ga={gaData}
      spacingClasses={spacingClasses}
      SectionWrapperComponent={SectionWrapperComponent}
      SliderComponent={SliderComponent}
    />
  ),
  ad: section => {
    if (!(section && section.config)) {
      return null;
    }
    const { placeholderType, adType } = section.config;

    const isCTN = adType && adType === 'ctn';
    const adWapperClass = placeholderType
      ? `paisa-wrapper ${placeholderType}_placeHolder`
      : '';

    return (
      <AdCaller
        data={section?.config}
        isCTN={isCTN}
        adWapperClass={adWapperClass}
      />
    );
  },
  iframe: section => {
    if (!(section && section.config && section.config.iframe)) {
      return null;
    }
    return <IFrame {...section.config.iframe} key={section.tn} />;
  },
  banner: section => <Banner data={section} key={section.type} />,
  minitv: section => (
    <div className="minitv_story_list">
      <WithMiniTv {...section.config.minitv} isInPageDock />
    </div>
  ),
};

const StoryList = ({
  config = {},
  items = [],
  dataConfig = {},
  isMobile,
  data,
  ga,
  SectionWrapperComponent,
  SliderComponent,
  shouldNotlazyloadLeadImage,
  location,
  hideTimeStamp,
}) => {
  const { inserts, spacing, border, layout = 'vertical' } = config;

  const typeShemaClassNames = getSchemaStyles(spacing, isMobile, '');
  const spacingClassNames = getSpacingStyles(spacing, isMobile, '', layout);
  const borderClassNames = getBorderStyles(border);

  const gaData = ga || config.ga;

  const header = config?.section?.header || {};
  const tabs = header?.tabs;
  const footer = config?.section?.footer;

  let finalItems = Array.isArray(data) && items.length === 0 ? data : items;

  const paginationData = data && data.pagination;

  const { start, end } = dataConfig;
  let startIndex = 1;

  if (start && end && Array.isArray(finalItems)) {
    finalItems =
      start === end
        ? [finalItems[start - 1]]
        : finalItems.slice(start - 1, end);
    startIndex = Number(start);
  } else if (start && Array.isArray(finalItems)) {
    finalItems = finalItems.slice(start - 1);
    startIndex = Number(start);
  }

  const [loading, setLoading] = useState(false);
  const [itemsToRender, setItemsToRender] = useState([]);
  const [isAjaxDataAtive, setIsAjaxDataAtive] = useState(false);
  const [hideMoreStories, setHideMoreStories] = useState(false);

  const storiesData = isAjaxDataAtive ? itemsToRender : finalItems;

  const loadSectionData = (dataUrl, ajaxCall, appendData) => {
    if (!dataUrl) {
      return;
    }
    setLoading(true);
    makeRequest.get(dataUrl, {}, 'skipfeedengine').then(response => {
      if (
        response &&
        response.data &&
        ((response.data.items && response.data.items.length > 0) ||
          response.data?.length > 0)
      ) {
        const responseItems =
          Array.isArray(response.data.items) && response.data.items.length > 0
            ? response.data.items
            : response.data;
        setItemsToRender(
          appendData && Array.isArray(storiesData)
            ? [...storiesData, ...responseItems]
            : responseItems,
        );
        setIsAjaxDataAtive(ajaxCall);
        setLoading(false);
      } else {
        setLoading(false);
        if (appendData) {
          setHideMoreStories(true);
        }
      }
    });
  };

  const renderCards = (itemConfig, isSlider = false) => {
    const { imageWidth } = config;
    const { cardSpan = 12, settings, layouts } = itemConfig;
    const isCarousel = itemConfig.carousel;

    const settingsConfig = {
      slidesToShow: Math.floor(12 / cardSpan),
      slidesToScroll: Math.floor(12 / cardSpan),
      arrows: !isMobile,
      dots: true,
      infinite: false,
      ...settings,
    };

    const cardItems = storiesData;
    if (!(Array.isArray(cardItems) && cardItems.length > 0)) return null;

    let cardsLayout = layout;
    if (layout === 'slider') {
      cardsLayout = 'horizontal';
    }
    const cardsListWrapperClass = classNames({
      [s.scroll]: isCarousel,
      crs: isCarousel,
      [s.slider]: layout === 'slider',
      [`${`${cardsLayout}_${cardSpan}`}`]: cardsLayout === 'horizontal',
      vertical_12: cardsLayout === 'vertical',
      vertical_list_padding:
        cardsLayout === 'vertical' && cardItems.length === 1,
    });

    const cardsComponent = cardItems.map((item, index) => {
      const layoutReplacement =
        Array.isArray(layouts) &&
        layouts.find(
          layoutItem =>
            layoutItem &&
            Array.isArray(layoutItem.positions) &&
            layoutItem.positions.includes(index + 1),
        );
      let cardWrapperClasses = classNames({
        [s.card_wrapper]: true,
      });
      if (
        index === 0 &&
        borderClassNames &&
        layoutReplacement &&
        layoutReplacement.layout &&
        leadCardLayouts.includes(layoutReplacement.layout)
      ) {
        cardWrapperClasses = classNames(cardWrapperClasses, 'brdr_skip');
      }
      const cardConfig =
        layoutReplacement && layoutReplacement.layout
          ? {
              ...itemConfig,
              layout: layoutReplacement.layout,
            }
          : itemConfig;
      return (
        <React.Fragment>
          {Array.isArray(inserts) &&
            inserts.map(insertItem => {
              if (insertItem.position === index + 1 && insertItem.component) {
                const insertComp =
                  typeof componentMap[insertItem.component.type] ===
                    'function' &&
                  componentMap[insertItem.component.type](
                    insertItem.component,
                    0,
                    storiesData,
                    gaData,
                  );
                return <div className={cardWrapperClasses}>{insertComp}</div>;
              }
              return null;
            })}
          <div className={cardWrapperClasses}>
            <StoryCard
              data={item}
              {...cardConfig}
              index={startIndex + index}
              ga={gaData}
              imgWidth={imageWidth}
              hideTimeStamp={hideTimeStamp}
              shouldNotlazyloadImage={
                shouldNotlazyloadLeadImage && startIndex + index === 1
              }
            />
          </div>
        </React.Fragment>
      );
    });
    if (isSlider && typeof SliderComponent === 'function') {
      return (
        <SliderComponent
          className={`${cardsListWrapperClass} ${spacingClassNames} slick_UI`}
          {...settingsConfig}
        >
          {cardsComponent}
        </SliderComponent>
      );
    }
    return (
      <div
        className={`${cardsListWrapperClass} ${spacingClassNames} ${borderClassNames}`}
        data-test={`${`${cardsLayout}_${cardSpan}`}`}
      >
        {cardsComponent}
      </div>
    );
  };

  const renderComponent = () => {
    switch (config.layout) {
      case 'grid':
        return componentMap.grid(
          config.grid,
          0,
          isMobile,
          storiesData,
          gaData,
          spacingClassNames,
          SectionWrapperComponent,
          SliderComponent,
          hideTimeStamp,
        );
      case 'slider':
        return renderCards(config.slider, true);
      default:
        if (config.card) {
          return renderCards(config.card);
        }
        if (config.type && typeof componentMap[config.type] === 'function') {
          return componentMap[config.type](config, 0, isMobile);
        }
    }
    return null;
  };

  const { wrapperClass } = config;

  const isCustomStylesPresent = wrapperClass;

  const storyListingComponent = isCustomStylesPresent ? (
    <div className={wrapperClass}>{renderComponent()}</div>
  ) : (
    renderComponent()
  );

  let component = storyListingComponent;
  if (config?.section && typeof SectionWrapperComponent === 'function') {
    const { heading } = header;
    if (!heading || (heading && !heading.text)) {
      if (
        Array.isArray(storiesData) &&
        storiesData.length > 0 &&
        storiesData[0] &&
        storiesData[0].template_name
      )
        header.heading = {
          text: storiesData[0].template_name,
          link: heading ? heading.link : '',
        };
    }
    component = !isMobile ? (
      <SectionWrapperComponent
        onTabsChangeCallback={loadSectionData}
        header={header}
        tabs={tabs}
        footer={footer}
        hideMoreStories={hideMoreStories}
        ga={gaData}
        loading={loading}
        typeShemaClassNames={typeShemaClassNames}
        paginationData={paginationData}
        location={location}
      >
        {storyListingComponent}
      </SectionWrapperComponent>
    ) : (
      <SectionWrapperComponent
        onTabsChangeCallback={loadSectionData}
        header={header}
        tabs={tabs}
        footer={footer}
        hideMoreStories={hideMoreStories}
        ga={gaData}
        loading={loading}
        typeShemaClassNames={typeShemaClassNames}
        paginationData={paginationData}
        location={location}
      >
        {storyListingComponent}
      </SectionWrapperComponent>
    );
  }

  const intersectionComp = isMobile ? (
    <div className={`${s.mobile_intersection} ${typeShemaClassNames}`} />
  ) : (
    <span className={`${s.web_intersection} ${typeShemaClassNames}`} />
  );
  return (
    <>
      {config &&
      Array.isArray(config.intersection_border) &&
      config.intersection_border.includes('top')
        ? intersectionComp
        : null}
      {config.ga && config.ga.type ? (
        <InViewObserver
          inViewCallback={() => {
            analyticsWrapper('ga4', {
              event: 'user_shown',
              type: config.ga.type,
              label: window && window?.location?.href,
            });
          }}
        >
          {component}
        </InViewObserver>
      ) : (
        component
      )}
      {config &&
      Array.isArray(config.intersection_border) &&
      config.intersection_border.includes('bottom')
        ? intersectionComp
        : null}
    </>
  );
};

StoryList.propTypes = {
  config: PropTypes.shape({
    inserts: PropTypes.arrayOf(PropTypes.shape({})),
    section: PropTypes.shape({}),
    card: PropTypes.shape({}),
    layout: PropTypes.string,
    columnsPerRow: PropTypes.number,
    wrapperClass: PropTypes.string,
  }),
  items: PropTypes.arrayOf(PropTypes.shape({})),
  dataConfig: PropTypes.shape({
    start: PropTypes.number,
    end: PropTypes.number,
  }),
  data: PropTypes.arrayOf(PropTypes.shape({})),
  isMobile: PropTypes.bool,
  ga: PropTypes.shape({}),
  SectionWrapperComponent: PropTypes.func,
  SliderComponent: PropTypes.func,
  shouldNotlazyloadLeadImage: PropTypes.bool,
  location: PropTypes.string,
  hideTimeStamp: PropTypes.bool,
};

StoryList.defaultProps = {
  config: {},
  items: [],
  dataConfig: {},
  isMobile: false,
  data: undefined,
  ga: undefined,
  SectionWrapperComponent: undefined,
  SliderComponent: undefined,
  shouldNotlazyloadLeadImage: false,
  location: '',
  hideTimeStamp: false,
};

export default StoryList;
